import React, { createContext, useState, useContext, useEffect } from 'react';
import { apiClient, ENDPOINTS } from '../services/apiConfig';

interface AuthContextType {
  currentUser: any;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (resetToken: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: any) => void;
  isAuthenticated: boolean;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for existing user on mount
  useEffect(() => {
    const checkUser = async () => {
      const userJson = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      
      if (userJson && token) {
        try {
          const user = JSON.parse(userJson);
          setCurrentUser(user);
          setIsAuthenticated(true);
          
          // Optionally verify token with backend
          try {
            const response = await apiClient.get(ENDPOINTS.AUTH.PROFILE);
            
            if (response.data) {
              // Update user data with latest from server
              setCurrentUser(response.data);
              localStorage.setItem('user', JSON.stringify(response.data));
            }
          } catch (error) {
            console.error('Token verification failed:', error);
            // Handle token expiration or other auth issues
            logout();
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setCurrentUser(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    checkUser();
  }, []);

  const login = async (email: string, password: string) => {
    try {
     // const response = await axios.post('http://localhost:5000/api/users/login', {
        const response = await apiClient.post(ENDPOINTS.AUTH.LOGIN, {
        email,
        password
      });
      
      console.log('Login response:', response.data);
      
      // Check if the response has the expected structure
      if (!response.data || !response.data.token) {
        throw new Error('Invalid response from server');
      }
      
      // Extract user data and token
      const userData = response.data;
      const token = response.data.token;
      
      // Save user data and token to localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', token);
      
      // Update state
      setCurrentUser(userData);
      setIsAuthenticated(true);
      
      return userData;
    } catch (error) {
      console.error('Login error in context:', error);
      throw error;
    }
  };

  const register = async (username: string, email: string, password: string) => {
    try {
        //const response = await axios.post('http://localhost:5000/api/users', {
        const response = await apiClient.post(ENDPOINTS.AUTH.REGISTER, {
        username,
        email,
        password
      });
      
      const data = response.data;
      
      // Save user data and token to localStorage
      localStorage.setItem('user', JSON.stringify(data.data));
      localStorage.setItem('token', data.data.token);
      
      // Update state
      setCurrentUser(data.data);
      setIsAuthenticated(true);
      
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  // Add forgotPassword function
const forgotPassword = async (email: string) => {
  try {
    const response = await apiClient.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
    return response.data;
  } catch (error) {
    console.error('Error in forgotPassword:', error);
    throw error;
  }
};

  // Add resetPassword function
  const resetPassword = async (resetToken: string, password: string) => {
    try {
      // const response = await axios.post(`http://localhost:5000/api/users/reset-password/${resetToken}`, {
        const response = await apiClient.post(ENDPOINTS.AUTH.RESET_PASSWORD, {
        password
      });
      
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  // Fix the logout function to remove the correct items from localStorage
  const logout = () => {
    setCurrentUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const updateUser = (userData: any) => {
    setCurrentUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const value = {
    currentUser,
    login,
    register,
    forgotPassword,
    resetPassword,
    logout,
    updateUser,
    isAuthenticated,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};